import { useCallback } from 'react';
import { TenantCountryCodeEnum } from '../api';
import { useTenant } from '../module/me';

export function withForceKeycloak(url: string, path?: string): string {
  const parsedUrl = new URL(url);

  if (path) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const parsedPath = new URL(normalizedPath, parsedUrl.origin);
    parsedUrl.pathname = parsedPath.pathname;
    parsedUrl.search = parsedPath.search;
    parsedUrl.hash = parsedPath.hash;
  }

  parsedUrl.searchParams.set('force_keycloak', 'true');
  return parsedUrl.toString();
}

function readViteEnv(key: string): string | undefined {
  return (import.meta.env as unknown as Record<string, string | undefined>)[key];
}

function getBikeleasingPortalUrl(countryCode?: string): string {
  const defaultUrl = readViteEnv('VITE_BIKELEASING_PORTAL_URL');
  const deploymentEnvironment = readViteEnv('VITE_SENTRY_ENVIRONMENT');
  const isProductionDeployment = deploymentEnvironment ? deploymentEnvironment === 'production' : !!import.meta.env.PROD;

  if (isProductionDeployment && countryCode === TenantCountryCodeEnum.At) {
    return readViteEnv('VITE_BIKELEASING_PORTAL_URL_AT') ?? 'https://portal.bikeleasing.at';
  }

  return defaultUrl ?? '';
}

export function getBikeleasingWebsiteUrl(countryCode?: string): string {
  const defaultUrl = readViteEnv('VITE_BIKELEASING_WEBSITE_URL');

  if (countryCode === TenantCountryCodeEnum.At) {
    return readViteEnv('VITE_BIKELEASING_WEBSITE_URL_AT') ?? 'https://bikeleasing.at';
  }

  return defaultUrl ?? '';
}

export function useBikeleasingWebsiteUrl(): string {
  const { tenant } = useTenant();
  return getBikeleasingWebsiteUrl(tenant?.countryCode);
}

export function withPreselectedRole(url: string, role: string): string {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set('preselected_role', role);
  return parsedUrl.toString();
}

export function withForceKeycloakBikeleasing(role: string, countryCode?: string, path?: string): string {
  const portalUrl = getBikeleasingPortalUrl(countryCode);
  if (!portalUrl) {
    return '';
  }
  return withPreselectedRole(withForceKeycloak(portalUrl, path), role);
}

export function useBikeleasingPortalLinkBuilder(role: string): (path?: string) => string {
  const { tenant } = useTenant();
  const countryCode = tenant?.countryCode;

  return useCallback((path?: string) => withForceKeycloakBikeleasing(role, countryCode, path), [role, countryCode]);
}

export function getBikeleasingInsuranceUrl(pimCoreUrl: string | undefined, countryCode?: string, langCode?: string): string {
  if (!pimCoreUrl) {
    return '';
  }
  if (countryCode === TenantCountryCodeEnum.At) {
    return `${pimCoreUrl}/get-asset/PDF/EN/Austria/insurance-and-service-manual-at-en.pdf`;
  }
  if (langCode?.toUpperCase().substring(0, 2) === 'DE') {
    return `${pimCoreUrl}/get-asset/PDF/DE/Versicherungshandbuch_DE.pdf`;
  }
  return `${pimCoreUrl}/get-asset/PDF/EN/Germany/insurance-and-service-manual-de-en.pdf`;
}
